import { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  MessageSquare, Send, X, AlertCircle, Loader2, 
  Flag, User, Building2, Newspaper, Info, ChevronDown,
  CheckCircle, Star, AlertTriangle, ExternalLink, ArrowDown,
  Clock, Calendar, MapPin, FileText, XCircle, Package, DollarSign
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from './ui/dialog';
import { Textarea } from './ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';

// Helper function to convert URLs to clickable links
const parseMessageWithLinks = (text) => {
  if (!text) return text;
  const urlRegex = /(https?:\/\/[^\s<]+[^<.,:;"')\]\s])/g;
  const parts = text.split(urlRegex);
  
  return parts.map((part, index) => {
    if (urlRegex.test(part)) {
      urlRegex.lastIndex = 0;
      return (
        <a 
          key={index}
          href={part} 
          target="_blank" 
          rel="noopener noreferrer"
          className="text-indigo-300 hover:text-indigo-200 underline underline-offset-2 inline-flex items-center gap-1"
          onClick={(e) => e.stopPropagation()}
          data-testid="chat-link"
        >
          {part.length > 35 ? part.substring(0, 35) + '...' : part}
          <ExternalLink size={11} className="inline flex-shrink-0" />
        </a>
      );
    }
    return part;
  });
};

// Cancellation reasons with time requirements
const CANCELLATION_REASONS = [
  { 
    id: 'date_time', 
    label: 'Disponibilidad fecha y hora', 
    hoursRequired: 48,
    description: 'Requiere 48hs de anticipación'
  },
  { 
    id: 'transport', 
    label: 'Sin disponibilidad de traslado', 
    hoursRequired: 24,
    description: 'Requiere 24hs de anticipación'
  },
  { 
    id: 'different_proposal', 
    label: 'Se comentó una propuesta distinta a la solicitada', 
    hoursRequired: 0,
    description: 'Sin restricción de tiempo'
  },
  { 
    id: 'other', 
    label: 'Otro motivo', 
    hoursRequired: 0,
    description: 'Detallar motivo'
  }
];

const ContractChat = ({ contract, userType, onClose, onContractUpdate }) => {
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [chatEnabled, setChatEnabled] = useState(true);
  const [readOnly, setReadOnly] = useState(false);
  const [disableReason, setDisableReason] = useState('');
  const [showReportModal, setShowReportModal] = useState(false);
  const [reportReason, setReportReason] = useState('');
  const [reportingStrike, setReportingStrike] = useState(false);
  const [showRules, setShowRules] = useState(false);
  const [otherUserTyping, setOtherUserTyping] = useState(false);
  const [showFinishRequestModal, setShowFinishRequestModal] = useState(false);
  const [showRatingModal, setShowRatingModal] = useState(false);
  const [showClaimModal, setShowClaimModal] = useState(false);
  const [showCancellationModal, setShowCancellationModal] = useState(false);
  const [rating, setRating] = useState(0);
  const [ratingComment, setRatingComment] = useState('');
  const [claimReason, setClaimReason] = useState('');
  const [cancellationReason, setCancellationReason] = useState('');
  const [cancellationDetails, setCancellationDetails] = useState('');
  const [submittingRating, setSubmittingRating] = useState(false);
  const [submittingClaim, setSubmittingClaim] = useState(false);
  const [submittingCancellation, setSubmittingCancellation] = useState(false);
  const [hasRated, setHasRated] = useState(false);
  const [showScrollButton, setShowScrollButton] = useState(false);
  const [respondingToFinish, setRespondingToFinish] = useState(false);
  const [showRejectReasonModal, setShowRejectReasonModal] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [showServiceInfo, setShowServiceInfo] = useState(true); // New state for service info panel
  const [typingTimeout, setTypingTimeout] = useState(null);
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (contract?.id) {
      fetchMessages();
      checkIfRated();
      if (!readOnly) {
        const interval = setInterval(fetchMessages, 5000);
        return () => clearInterval(interval);
      }
    }
  }, [contract?.id, readOnly]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Handle scroll to show/hide scroll button
  const handleScroll = () => {
    if (messagesContainerRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = messagesContainerRef.current;
      setShowScrollButton(scrollHeight - scrollTop - clientHeight > 100);
    }
  };

  const checkIfRated = async () => {
    try {
      const response = await api.get(`/press/contracts/${contract.id}/rating`);
      if (response.data?.has_rated) {
        setHasRated(true);
      }
    } catch (error) {
      // Rating not found is okay
    }
  };

  const sendTypingIndicator = useCallback(async () => {
    try {
      await api.post(`/press/contracts/${contract.id}/typing`);
    } catch (error) {
      // Ignore errors for typing indicator
    }
  }, [contract?.id]);

  const handleTyping = () => {
    if (typingTimeout) {
      clearTimeout(typingTimeout);
    }
    sendTypingIndicator();
    setTypingTimeout(setTimeout(() => {}, 3000));
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const fetchMessages = async () => {
    try {
      const response = await api.get(`/press/contracts/${contract.id}/messages`);
      setMessages(response.data.messages || []);
      setChatEnabled(response.data.chat_enabled || response.data.read_only);
      setReadOnly(response.data.read_only || false);
      setDisableReason(response.data.reason || '');
      setOtherUserTyping(response.data.other_user_typing || false);
    } catch (error) {
      console.error('Error fetching messages:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || sending) return;

    setSending(true);
    try {
      await api.post(`/press/contracts/${contract.id}/messages`, {
        content: newMessage.trim()
      });
      setNewMessage('');
      fetchMessages();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al enviar mensaje');
    } finally {
      setSending(false);
    }
  };

  const formatTime = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const isToday = date.toDateString() === now.toDateString();
    
    if (isToday) {
      return date.toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' });
    }
    return date.toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit' }) + 
           ' ' + date.toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' });
  };

  const isMyMessage = (msg) => {
    return msg.sender_type === userType;
  };

  const handleReportStrike = async () => {
    if (!reportReason.trim()) {
      toast.error('Describí el motivo del reporte');
      return;
    }

    setReportingStrike(true);
    try {
      await api.post('/press/strikes/report', {
        press_id: contract.press_id,
        reason: reportReason,
        evidence: `Conversación del contrato ${contract.id}`
      });
      
      toast.success('Reporte enviado. Será revisado por el equipo de moderación.');
      setShowReportModal(false);
      setReportReason('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al enviar reporte');
    } finally {
      setReportingStrike(false);
    }
  };

  const handleRequestFinish = async () => {
    try {
      await api.post(`/press/contracts/${contract.id}/request-finish`);
      toast.success('Solicitud de finalización enviada al productor');
      setShowFinishRequestModal(false);
      fetchMessages();
      if (onContractUpdate) onContractUpdate();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al enviar solicitud');
    }
  };

  const handleRespondToFinishRequest = async (accept, reason = null) => {
    setRespondingToFinish(true);
    try {
      const params = new URLSearchParams({ accept: accept.toString() });
      if (!accept && reason) {
        params.append('rejection_reason', reason);
      }
      const response = await api.post(
        `/press/contracts/${contract.id}/respond-finish-request?${params.toString()}`
      );
      
      if (accept) {
        toast.success('Trabajo marcado como completado');
      } else {
        toast.success('Solicitud de finalización rechazada');
        setShowRejectReasonModal(false);
        setRejectReason('');
      }
      
      fetchMessages();
      if (onContractUpdate) onContractUpdate();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al responder solicitud');
    } finally {
      setRespondingToFinish(false);
    }
  };

  const handleSubmitRating = async () => {
    if (rating === 0) {
      toast.error('Seleccioná una calificación');
      return;
    }

    setSubmittingRating(true);
    try {
      await api.post(`/press/contracts/${contract.id}/rating`, {
        rating,
        comment: ratingComment.trim()
      });
      toast.success('¡Gracias por tu calificación!');
      setShowRatingModal(false);
      setHasRated(true);
      if (onContractUpdate) onContractUpdate();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al enviar calificación');
    } finally {
      setSubmittingRating(false);
    }
  };

  const handleSubmitClaim = async () => {
    if (!claimReason.trim()) {
      toast.error('Describí el motivo del reclamo');
      return;
    }

    setSubmittingClaim(true);
    try {
      await api.post(`/press/contracts/${contract.id}/claim`, {
        reason: claimReason.trim()
      });
      toast.success('Reclamo enviado. Será revisado por el equipo de soporte.');
      setShowClaimModal(false);
      setClaimReason('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al enviar reclamo');
    } finally {
      setSubmittingClaim(false);
    }
  };

  // Handle cancellation request (arrepentimiento)
  const handleSubmitCancellation = async () => {
    if (!cancellationReason) {
      toast.error('Seleccioná un motivo de cancelación');
      return;
    }
    
    if (cancellationReason === 'other' && !cancellationDetails.trim()) {
      toast.error('Describí el motivo de cancelación');
      return;
    }

    const selectedReason = CANCELLATION_REASONS.find(r => r.id === cancellationReason);
    
    setSubmittingCancellation(true);
    try {
      await api.post(`/press/contracts/${contract.id}/cancellation-request`, {
        reason_type: cancellationReason,
        reason_label: selectedReason?.label,
        details: cancellationDetails.trim(),
        hours_required: selectedReason?.hoursRequired || 0
      });
      toast.success('Solicitud de cancelación enviada. Un administrador la revisará.');
      setShowCancellationModal(false);
      setCancellationReason('');
      setCancellationDetails('');
      if (onContractUpdate) onContractUpdate();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al enviar solicitud de cancelación');
    } finally {
      setSubmittingCancellation(false);
    }
  };

  return (
    <div 
      className="flex flex-col h-full bg-zinc-950 rounded-xl overflow-hidden border border-zinc-800 shadow-2xl ring-1 ring-white/5"
      data-testid="contract-chat"
    >
      {/* Header */}
      <div className="h-16 px-5 border-b border-zinc-800 bg-zinc-900/80 backdrop-blur-xl flex items-center justify-between z-10">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center shadow-lg shadow-indigo-500/20">
            {userType === 'producer' ? (
              <Newspaper className="text-white" size={18} />
            ) : (
              <Building2 className="text-white" size={18} />
            )}
          </div>
          <div>
            <h3 className="text-white font-semibold tracking-tight" data-testid="chat-title">
              {userType === 'producer' ? contract.press_name : contract.producer_name}
            </h3>
            <p className="text-zinc-500 text-xs font-medium">
              Contrato #{contract.id?.slice(-6).toUpperCase()}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="sm"
            className="text-zinc-400 hover:text-white hover:bg-zinc-800 h-9 w-9 p-0"
            onClick={() => setShowRules(!showRules)}
            data-testid="rules-toggle"
          >
            <Info size={18} />
          </Button>
          {userType === 'producer' && (
            <Button
              variant="ghost"
              size="sm"
              className="text-zinc-400 hover:text-red-400 hover:bg-red-500/10 h-9 w-9 p-0"
              onClick={() => setShowReportModal(true)}
              title="Reportar incumplimiento"
              data-testid="report-btn"
            >
              <Flag size={18} />
            </Button>
          )}
          {onClose && (
            <Button
              variant="ghost"
              size="sm"
              className="text-zinc-400 hover:text-white hover:bg-zinc-800 h-9 w-9 p-0"
              onClick={onClose}
              data-testid="close-chat-btn"
            >
              <X size={18} />
            </Button>
          )}
        </div>
      </div>

      {/* Rules Banner - Collapsible */}
      <AnimatePresence>
        {showRules && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="bg-indigo-500/5 border-b border-indigo-500/20 overflow-hidden"
          >
            <div className="p-4">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-lg bg-indigo-500/20 flex items-center justify-center flex-shrink-0">
                  <AlertCircle size={16} className="text-indigo-400" />
                </div>
                <div className="flex-1">
                  <h4 className="text-indigo-400 font-semibold text-sm mb-2">Normas de Comunicación</h4>
                  <ul className="text-zinc-400 text-xs space-y-1.5">
                    <li className="flex items-start gap-2">
                      <span className="w-1 h-1 rounded-full bg-zinc-600 mt-1.5 flex-shrink-0"></span>
                      Todas las comunicaciones deben realizarse dentro de la plataforma
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="w-1 h-1 rounded-full bg-zinc-600 mt-1.5 flex-shrink-0"></span>
                      Está prohibido compartir números de teléfono, WhatsApp o emails personales
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="w-1 h-1 rounded-full bg-zinc-600 mt-1.5 flex-shrink-0"></span>
                      Las conversaciones son monitoreadas para garantizar la seguridad
                    </li>
                  </ul>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-zinc-500 hover:text-white h-8 w-8 p-0"
                  onClick={() => setShowRules(false)}
                >
                  <X size={14} />
                </Button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Service Info Panel - Shows what was contracted */}
      <AnimatePresence>
        {showServiceInfo && (contract.package_ids?.length > 0 || contract.quote_message || contract.response_message) && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="bg-gradient-to-r from-emerald-500/5 to-blue-500/5 border-b border-emerald-500/20 overflow-hidden"
          >
            <div className="p-4">
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-8 h-8 rounded-lg bg-emerald-500/20 flex items-center justify-center flex-shrink-0">
                      <Package size={16} className="text-emerald-400" />
                    </div>
                    <h4 className="text-emerald-400 font-semibold text-sm">Servicio Contratado</h4>
                    <span className="ml-auto text-emerald-400 font-bold">${contract.total_amount?.toLocaleString()} ARS</span>
                  </div>
                  
                  {/* Packages contracted */}
                  {contract.package_ids?.length > 0 && (
                    <div className="mb-3">
                      <p className="text-zinc-500 text-xs mb-2 uppercase tracking-wider">Paquetes:</p>
                      <div className="flex flex-wrap gap-2">
                        {contract.package_ids.map((pkgId, idx) => (
                          <span 
                            key={idx}
                            className="px-3 py-1.5 bg-blue-500/10 text-blue-400 rounded-lg text-sm border border-blue-500/20 font-medium"
                          >
                            {pkgId}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* Original request from producer */}
                  {contract.quote_message && (
                    <div className="mb-3 p-3 bg-zinc-800/50 rounded-lg border-l-2 border-blue-500/50">
                      <p className="text-zinc-500 text-xs mb-1">Solicitud del {userType === 'producer' ? 'productor' : 'cliente'}:</p>
                      <p className="text-zinc-300 text-sm">{contract.quote_message}</p>
                    </div>
                  )}
                  
                  {/* Press response with details */}
                  {contract.response_message && (
                    <div className="p-3 bg-zinc-800/50 rounded-lg border-l-2 border-emerald-500/50">
                      <p className="text-zinc-500 text-xs mb-1">Acuerdo del {userType === 'press' ? 'medio' : 'proveedor'}:</p>
                      <p className="text-zinc-300 text-sm">{contract.response_message}</p>
                    </div>
                  )}
                  
                  {/* Event info if available */}
                  {contract.event_name && (
                    <div className="mt-3 flex items-center gap-2 text-zinc-400 text-xs">
                      <Calendar size={12} />
                      <span>Evento: {contract.event_name}</span>
                    </div>
                  )}
                </div>
                
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-zinc-500 hover:text-white h-8 w-8 p-0 flex-shrink-0"
                  onClick={() => setShowServiceInfo(false)}
                  title="Ocultar detalles"
                >
                  <X size={14} />
                </Button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Toggle Service Info Button (when hidden) */}
      {!showServiceInfo && (contract.package_ids?.length > 0 || contract.quote_message) && (
        <button
          onClick={() => setShowServiceInfo(true)}
          className="w-full py-2 px-4 bg-zinc-900/50 border-b border-zinc-800 text-zinc-500 text-xs hover:text-zinc-300 hover:bg-zinc-800/50 transition-colors flex items-center justify-center gap-2"
        >
          <Package size={12} />
          Ver detalles del servicio contratado
          <ChevronDown size={12} />
        </button>
      )}

      {/* Messages Area */}
      <div 
        ref={messagesContainerRef}
        onScroll={handleScroll}
        className="flex-1 overflow-y-auto p-5 space-y-4 scrollbar-thin scrollbar-thumb-zinc-800 scrollbar-track-transparent relative"
        data-testid="messages-container"
      >
        {loading ? (
          <div className="flex items-center justify-center h-full">
            <div className="flex flex-col items-center gap-3">
              <Loader2 className="animate-spin text-indigo-500" size={32} />
              <p className="text-zinc-500 text-sm">Cargando mensajes...</p>
            </div>
          </div>
        ) : !chatEnabled ? (
          <div className="flex flex-col items-center justify-center h-full text-center">
            <div className="w-16 h-16 rounded-2xl bg-zinc-800/50 flex items-center justify-center mb-4">
              <AlertCircle className="text-zinc-600" size={32} />
            </div>
            <p className="text-zinc-400 font-medium">{disableReason || 'El chat no está disponible'}</p>
          </div>
        ) : messages.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-center">
            <div className="w-16 h-16 rounded-2xl bg-zinc-800/50 flex items-center justify-center mb-4">
              <MessageSquare className="text-zinc-600" size={32} />
            </div>
            <p className="text-zinc-400 font-medium">No hay mensajes aún</p>
            <p className="text-zinc-600 text-sm mt-1">Comenzá la conversación para coordinar el trabajo</p>
          </div>
        ) : (
          <>
            {/* File sharing notice banner */}
            <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-3 mb-4" data-testid="file-sharing-notice">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 bg-blue-500/20 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                  <ExternalLink className="text-blue-400" size={16} />
                </div>
                <div>
                  <p className="text-blue-300 font-medium text-sm">Compartir archivos multimedia</p>
                  <p className="text-blue-400/70 text-xs mt-1">
                    Para compartir fotos, videos u otros archivos, utilizá links de servicios externos como 
                    <span className="font-medium text-blue-300"> Google Drive</span>, 
                    <span className="font-medium text-blue-300"> Dropbox</span>, 
                    <span className="font-medium text-blue-300"> WeTransfer</span> o similares.
                    Los links se mostrarán clickeables en el chat.
                  </p>
                </div>
              </div>
            </div>

            {messages.map((msg, index) => (
              <motion.div
                key={msg.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.2, delay: Math.min(index * 0.03, 0.3) }}
                className={`flex ${msg.sender_type === 'system' ? 'justify-center' : isMyMessage(msg) ? 'justify-end' : 'justify-start'}`}
                data-testid={`message-${msg.id}`}
              >
                {/* System messages */}
                {msg.sender_type === 'system' ? (
                  <div className="max-w-[90%]">
                    <div className="bg-zinc-900/50 border border-zinc-800/50 text-zinc-400 text-xs px-4 py-2 rounded-xl flex items-center gap-2 font-medium">
                      <AlertCircle size={12} />
                      <span>{msg.content}</span>
                    </div>
                    
                    {/* Show accept/reject buttons for finish request - only for producer */}
                    {msg.message_type === 'finish_request' && 
                     userType === 'producer' && 
                     contract.finish_request_status === 'pending' && (
                      <div className="flex justify-center gap-2 mt-3" data-testid="finish-request-actions">
                        <Button
                          size="sm"
                          className="bg-emerald-500 hover:bg-emerald-600 text-white text-xs"
                          onClick={() => handleRespondToFinishRequest(true)}
                          disabled={respondingToFinish}
                          data-testid="accept-finish-btn"
                        >
                          {respondingToFinish ? (
                            <Loader2 className="animate-spin mr-1" size={14} />
                          ) : (
                            <CheckCircle size={14} className="mr-1" />
                          )}
                          Confirmar Completado
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="border-red-500/30 text-red-400 hover:bg-red-500/10 text-xs"
                          onClick={() => setShowRejectReasonModal(true)}
                          disabled={respondingToFinish}
                          data-testid="reject-finish-btn"
                        >
                          <XCircle size={14} className="mr-1" />
                          Rechazar
                        </Button>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className={`max-w-[75%] ${isMyMessage(msg) ? 'order-2' : 'order-1'}`}>
                    <div
                      className={`relative px-4 py-3 rounded-2xl text-[15px] leading-relaxed shadow-sm ${
                        isMyMessage(msg)
                          ? 'bg-indigo-600 text-white rounded-br-md shadow-indigo-900/20'
                          : 'bg-zinc-800 border border-zinc-700/50 text-zinc-100 rounded-bl-md'
                      }`}
                    >
                      {!isMyMessage(msg) && (
                        <p className="text-xs font-semibold mb-1.5 text-indigo-400">
                          {msg.sender_name}
                        </p>
                      )}
                      <p className="whitespace-pre-wrap break-words">{parseMessageWithLinks(msg.content)}</p>
                      <span className={`text-[10px] mt-1.5 block ${isMyMessage(msg) ? 'text-indigo-200/70' : 'text-zinc-500'}`}>
                        {formatTime(msg.created_at)}
                        {isMyMessage(msg) && msg.read && (
                          <span className="ml-1.5">✓✓</span>
                        )}
                      </span>
                    </div>
                  </div>
                )}
              </motion.div>
            ))}
            
            {/* Typing indicator */}
            <AnimatePresence>
              {otherUserTyping && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  className="flex justify-start"
                  data-testid="typing-indicator"
                >
                  <div className="flex items-center gap-1.5 px-4 py-3 bg-zinc-800/50 border border-zinc-700/30 rounded-2xl rounded-bl-md">
                    <span className="w-1.5 h-1.5 bg-zinc-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></span>
                    <span className="w-1.5 h-1.5 bg-zinc-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></span>
                    <span className="w-1.5 h-1.5 bg-zinc-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></span>
                    <span className="text-xs text-zinc-500 ml-1 font-medium">escribiendo</span>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
            <div ref={messagesEndRef} />
          </>
        )}

        {/* Scroll to bottom button */}
        <AnimatePresence>
          {showScrollButton && (
            <motion.button
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.8 }}
              onClick={scrollToBottom}
              className="fixed bottom-24 right-8 w-10 h-10 bg-indigo-600 hover:bg-indigo-700 text-white rounded-full shadow-lg shadow-indigo-900/30 flex items-center justify-center transition-colors z-20"
              data-testid="scroll-to-bottom"
            >
              <ArrowDown size={18} />
            </motion.button>
          )}
        </AnimatePresence>
      </div>

      {/* Input Area */}
      {chatEnabled && !readOnly && (
        <div className="p-4 bg-zinc-900/90 border-t border-zinc-800 backdrop-blur-lg">
          {/* Action buttons for active contracts */}
          {(contract.status === 'active' || contract.status === 'in_progress') && (
            <div className="flex gap-2 mb-3 flex-wrap">
              {userType === 'press' && (
                <Button
                  type="button"
                  size="sm"
                  className="bg-amber-500/10 text-amber-400 hover:bg-amber-500/20 border border-amber-500/20 text-xs"
                  onClick={() => setShowFinishRequestModal(true)}
                  data-testid="request-finish-btn"
                >
                  <CheckCircle size={14} className="mr-1.5" />
                  Solicitar Finalización
                </Button>
              )}
              <Button
                type="button"
                size="sm"
                className="bg-red-500/10 text-red-400 hover:bg-red-500/20 border border-red-500/20 text-xs"
                onClick={() => setShowCancellationModal(true)}
                data-testid="cancellation-btn"
              >
                <XCircle size={14} className="mr-1.5" />
                Solicitar Cancelación
              </Button>
            </div>
          )}
          <form onSubmit={handleSendMessage} className="flex gap-3">
            <div className="flex-1 relative">
              <Input
                ref={inputRef}
                value={newMessage}
                onChange={(e) => {
                  setNewMessage(e.target.value);
                  handleTyping();
                }}
                placeholder="Escribí un mensaje..."
                className="bg-zinc-800/50 border-zinc-700/50 focus:border-indigo-500/50 focus:ring-2 focus:ring-indigo-500/20 rounded-xl pr-4 h-11 text-[15px] placeholder:text-zinc-600"
                disabled={sending}
                data-testid="message-input"
              />
            </div>
            <Button
              type="submit"
              disabled={!newMessage.trim() || sending}
              className="bg-indigo-600 hover:bg-indigo-700 text-white shadow-lg shadow-indigo-900/20 h-11 px-5 rounded-xl"
              data-testid="send-message-btn"
            >
              {sending ? (
                <Loader2 className="animate-spin" size={18} />
              ) : (
                <Send size={18} />
              )}
            </Button>
          </form>
        </div>
      )}
      
      {/* Read-only banner for completed contracts */}
      {readOnly && (
        <div className="p-4 border-t border-zinc-800 bg-emerald-500/5">
          <p className="text-emerald-400 text-sm flex items-center justify-center gap-2 mb-3 font-medium">
            <CheckCircle size={16} />
            {disableReason || 'Historial de conversación - Solo lectura'}
          </p>
          {contract.status === 'completed' && (
            <div className="flex gap-2 justify-center flex-wrap">
              {!hasRated && (
                <Button
                  size="sm"
                  className="bg-amber-500/10 text-amber-400 hover:bg-amber-500/20 border border-amber-500/20"
                  onClick={() => setShowRatingModal(true)}
                  data-testid="rate-btn"
                >
                  <Star size={14} className="mr-1.5" />
                  Calificar Experiencia
                </Button>
              )}
              <Button
                size="sm"
                className="bg-red-500/10 text-red-400 hover:bg-red-500/20 border border-red-500/20"
                onClick={() => setShowClaimModal(true)}
                data-testid="claim-btn"
              >
                <AlertTriangle size={14} className="mr-1.5" />
                Reclamar
              </Button>
            </div>
          )}
        </div>
      )}

      {/* Report Strike Modal */}
      <Dialog open={showReportModal} onOpenChange={setShowReportModal}>
        <DialogContent className="bg-zinc-950 border-zinc-800 sm:rounded-xl">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Flag className="text-red-400" size={20} />
              Reportar Incumplimiento
            </DialogTitle>
            <DialogDescription className="text-zinc-500">
              Reportá si el medio incumplió las normas de comunicación
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="bg-red-500/5 border border-red-500/20 rounded-xl p-4">
              <p className="text-red-400 text-sm">
                Solicitó información de contacto personal, compartió WhatsApp, teléfono o email,
                u otro comportamiento prohibido según las normas.
              </p>
            </div>
            
            <div>
              <label className="block text-sm text-zinc-400 mb-2 font-medium">Motivo del reporte</label>
              <Textarea
                value={reportReason}
                onChange={(e) => setReportReason(e.target.value)}
                placeholder="Describí qué norma se incumplió..."
                className="bg-zinc-900 border-zinc-800 focus:border-red-500/50 min-h-[100px] rounded-xl"
              />
            </div>
            
            <div className="text-zinc-500 text-xs">
              Los reportes falsos o malintencionados pueden resultar en sanciones correspondientes.
            </div>
          </div>

          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowReportModal(false)} className="hover:bg-zinc-800">
              Cancelar
            </Button>
            <Button
              onClick={handleReportStrike}
              disabled={reportingStrike || !reportReason.trim()}
              className="bg-red-500 hover:bg-red-600 text-white"
            >
              {reportingStrike ? (
                <>
                  <Loader2 className="animate-spin mr-2" size={16} />
                  Enviando...
                </>
              ) : (
                <>
                  <Flag size={16} className="mr-2" />
                  Enviar Reporte
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Finish Request Modal */}
      <Dialog open={showFinishRequestModal} onOpenChange={setShowFinishRequestModal}>
        <DialogContent className="bg-zinc-950 border-zinc-800 sm:rounded-xl">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <CheckCircle className="text-amber-400" size={20} />
              Solicitar Finalización del Trabajo
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="bg-amber-500/5 border border-amber-500/20 rounded-xl p-4">
              <p className="text-amber-400 text-sm">
                Al enviar esta solicitud, <strong>{contract.producer_name}</strong> recibirá una notificación 
                para confirmar que el trabajo fue completado satisfactoriamente.
              </p>
            </div>
            
            <p className="text-zinc-400 text-sm">
              Una vez que la productora confirme, el contrato se marcará como completado y 
              ambos podrán calificar su experiencia.
            </p>
          </div>

          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowFinishRequestModal(false)} className="hover:bg-zinc-800">
              Cancelar
            </Button>
            <Button
              onClick={handleRequestFinish}
              className="bg-amber-500 hover:bg-amber-600 text-black font-medium"
            >
              <CheckCircle size={16} className="mr-2" />
              Enviar Solicitud
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Rating Modal */}
      <Dialog open={showRatingModal} onOpenChange={setShowRatingModal}>
        <DialogContent className="bg-zinc-950 border-zinc-800 sm:rounded-xl">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Star className="text-amber-400" size={20} />
              Calificar Experiencia
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <p className="text-zinc-400 text-sm">
              ¿Cómo fue tu experiencia trabajando con{' '}
              <strong className="text-white">
                {userType === 'producer' ? contract.press_name : contract.producer_name}
              </strong>?
            </p>
            
            {/* Star Rating */}
            <div className="flex justify-center gap-2 py-4">
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  type="button"
                  onClick={() => setRating(star)}
                  className="transition-all hover:scale-110 focus:scale-110 focus:outline-none"
                  data-testid={`star-${star}`}
                >
                  <Star 
                    size={36} 
                    className={`transition-colors ${star <= rating ? 'text-amber-400 fill-amber-400' : 'text-zinc-700 hover:text-zinc-600'}`}
                  />
                </button>
              ))}
            </div>
            
            <div>
              <label className="block text-sm text-zinc-400 mb-2 font-medium">Comentario (opcional)</label>
              <Textarea
                value={ratingComment}
                onChange={(e) => setRatingComment(e.target.value)}
                placeholder="Contanos más sobre tu experiencia..."
                className="bg-zinc-900 border-zinc-800 min-h-[100px] rounded-xl"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowRatingModal(false)} className="hover:bg-zinc-800">
              Cancelar
            </Button>
            <Button
              onClick={handleSubmitRating}
              disabled={submittingRating || rating === 0}
              className="bg-amber-500 hover:bg-amber-600 text-black font-medium"
            >
              {submittingRating ? (
                <>
                  <Loader2 className="animate-spin mr-2" size={16} />
                  Enviando...
                </>
              ) : (
                <>
                  <Star size={16} className="mr-2" />
                  Enviar Calificación
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Claim Modal */}
      <Dialog open={showClaimModal} onOpenChange={setShowClaimModal}>
        <DialogContent className="bg-zinc-950 border-zinc-800 sm:rounded-xl">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <AlertTriangle className="text-red-400" size={20} />
              Presentar Reclamo
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="bg-red-500/5 border border-red-500/20 rounded-xl p-4">
              <p className="text-red-400 text-sm">
                Usá esta opción si tuviste algún problema con el servicio o necesitás 
                que nuestro equipo intervenga en la resolución de un conflicto.
              </p>
            </div>
            
            <div>
              <label className="block text-sm text-zinc-400 mb-2 font-medium">Motivo del reclamo *</label>
              <Textarea
                value={claimReason}
                onChange={(e) => setClaimReason(e.target.value)}
                placeholder="Describí detalladamente el problema o inconveniente..."
                className="bg-zinc-900 border-zinc-800 min-h-[120px] rounded-xl"
              />
            </div>
            
            <p className="text-zinc-500 text-xs">
              Nuestro equipo de soporte revisará tu reclamo y se comunicará contigo a la brevedad.
            </p>
          </div>

          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowClaimModal(false)} className="hover:bg-zinc-800">
              Cancelar
            </Button>
            <Button
              onClick={handleSubmitClaim}
              disabled={submittingClaim || !claimReason.trim()}
              className="bg-red-500 hover:bg-red-600 text-white"
            >
              {submittingClaim ? (
                <>
                  <Loader2 className="animate-spin mr-2" size={16} />
                  Enviando...
                </>
              ) : (
                <>
                  <AlertTriangle size={16} className="mr-2" />
                  Enviar Reclamo
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cancellation Request Modal (Arrepentimiento) */}
      <Dialog open={showCancellationModal} onOpenChange={setShowCancellationModal}>
        <DialogContent className="bg-zinc-950 border-zinc-800 sm:rounded-xl max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <XCircle className="text-amber-400" size={20} />
              Solicitar Cancelación
            </DialogTitle>
            <DialogDescription className="text-zinc-500">
              Tu solicitud será revisada por un administrador
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="bg-amber-500/5 border border-amber-500/20 rounded-xl p-4">
              <p className="text-amber-400 text-sm flex items-start gap-2">
                <AlertCircle size={16} className="mt-0.5 flex-shrink-0" />
                Las cancelaciones están sujetas a revisión y deben cumplir con los tiempos de anticipación requeridos.
              </p>
            </div>
            
            <div>
              <label className="block text-sm text-zinc-400 mb-2 font-medium">Motivo de cancelación *</label>
              <Select value={cancellationReason} onValueChange={setCancellationReason}>
                <SelectTrigger className="bg-zinc-900 border-zinc-800 h-11 rounded-xl" data-testid="cancellation-reason-select">
                  <SelectValue placeholder="Seleccioná un motivo" />
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-800">
                  {CANCELLATION_REASONS.map((reason) => (
                    <SelectItem 
                      key={reason.id} 
                      value={reason.id}
                      className="focus:bg-zinc-800"
                    >
                      <div className="flex flex-col items-start">
                        <span>{reason.label}</span>
                        <span className="text-xs text-zinc-500">{reason.description}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Time requirement warning */}
            {cancellationReason && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="flex items-center gap-2 text-sm"
              >
                {(() => {
                  const selected = CANCELLATION_REASONS.find(r => r.id === cancellationReason);
                  if (selected?.hoursRequired > 0) {
                    return (
                      <div className="flex items-center gap-2 text-blue-400 bg-blue-500/10 px-3 py-2 rounded-lg w-full">
                        <Clock size={14} />
                        <span>Requiere {selected.hoursRequired}hs de anticipación al evento</span>
                      </div>
                    );
                  }
                  return null;
                })()}
              </motion.div>
            )}
            
            {/* Details field - always shown for 'other', optional for rest */}
            <div>
              <label className="block text-sm text-zinc-400 mb-2 font-medium">
                {cancellationReason === 'other' ? 'Detalle del motivo *' : 'Información adicional (opcional)'}
              </label>
              <Textarea
                value={cancellationDetails}
                onChange={(e) => setCancellationDetails(e.target.value)}
                placeholder={cancellationReason === 'other' 
                  ? "Describí detalladamente el motivo de la cancelación..."
                  : "Agregá información que ayude a procesar tu solicitud..."
                }
                className="bg-zinc-900 border-zinc-800 min-h-[100px] rounded-xl"
                data-testid="cancellation-details"
              />
            </div>
            
            <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-3">
              <p className="text-zinc-500 text-xs">
                Un administrador revisará tu solicitud y la procesará según corresponda. 
                Te notificaremos el resultado.
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowCancellationModal(false)} className="hover:bg-zinc-800">
              Cancelar
            </Button>
            <Button
              onClick={handleSubmitCancellation}
              disabled={submittingCancellation || !cancellationReason || (cancellationReason === 'other' && !cancellationDetails.trim())}
              className="bg-amber-500 hover:bg-amber-600 text-black font-medium"
              data-testid="submit-cancellation-btn"
            >
              {submittingCancellation ? (
                <>
                  <Loader2 className="animate-spin mr-2" size={16} />
                  Enviando...
                </>
              ) : (
                <>
                  <XCircle size={16} className="mr-2" />
                  Enviar Solicitud
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reject Finish Request Modal */}
      <Dialog open={showRejectReasonModal} onOpenChange={setShowRejectReasonModal}>
        <DialogContent className="bg-zinc-950 border-zinc-800 sm:rounded-xl">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <XCircle className="text-red-400" size={20} />
              Rechazar Solicitud de Finalización
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <p className="text-zinc-400 text-sm">
              Indicá el motivo por el cual rechazás la solicitud de finalización del trabajo.
            </p>
            
            <div>
              <label className="block text-sm text-zinc-400 mb-2 font-medium">Motivo del rechazo *</label>
              <Textarea
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder="Ej: El trabajo aún no está completo, falta entregar..."
                className="bg-zinc-900 border-zinc-800 focus:border-red-500/50 min-h-[100px] rounded-xl"
                data-testid="reject-reason-input"
              />
            </div>
            
            <div className="bg-amber-500/5 border border-amber-500/20 rounded-xl p-3">
              <p className="text-amber-400 text-xs">
                El medio de prensa recibirá tu respuesta y podrá volver a solicitar la finalización cuando el trabajo esté completo.
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowRejectReasonModal(false)} className="hover:bg-zinc-800">
              Cancelar
            </Button>
            <Button
              onClick={() => handleRespondToFinishRequest(false, rejectReason)}
              disabled={respondingToFinish || !rejectReason.trim()}
              className="bg-red-500 hover:bg-red-600 text-white"
              data-testid="confirm-reject-btn"
            >
              {respondingToFinish ? (
                <>
                  <Loader2 className="animate-spin mr-2" size={16} />
                  Enviando...
                </>
              ) : (
                <>
                  <XCircle size={16} className="mr-2" />
                  Rechazar Solicitud
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ContractChat;
